/**
 * Entrada do operador, em duas colunas: o formulário à esquerda e a marca à
 * direita, sobre um painel escuro com grade.
 *
 * Zod valida antes de chamar a API: erro de digitação vira mensagem imediata em
 * vez de uma ida ao servidor para ouvir 400.
 *
 * ## O que o gabarito tem e esta tela não
 *
 * O modelo traz entrar com Google, entrar com X, "manter conectado", "esqueci a
 * senha" e "criar conta". **Nenhum desses fluxos existe aqui**, e botão que não
 * faz nada é pior que botão ausente — quem clica descobre sozinho, no momento em
 * que precisava entrar.
 *
 * Credencial de operador é criada pelo administrador (`make credencial`), e é
 * isso que o rodapé diz, em vez de um "criar conta" que levaria a lugar nenhum.
 *
 * ## O freio de tentativas
 *
 * Atraso progressivo, bloqueio por conta e limite por janela vivem em
 * `lib/login-guard.ts`. Três coisas que esta tela faz por causa dele:
 *
 * 1. **o `submit` confere antes de enviar.** Botão desabilitado não é trava:
 *    `Enter` no campo, `form.requestSubmit()` do console ou o atributo removido
 *    no inspetor passariam por ele. A conferência está no caminho, não na
 *    aparência;
 * 2. **só 401 conta como falha.** Rede caída e 500 são problema de
 *    infraestrutura; transformá-los em dez minutos de bloqueio faria o operador
 *    jurar que a senha certa não funciona;
 * 3. **o tempo que falta aparece e anda.** Botão morto sem explicação é tela
 *    quebrada para quem usa.
 *
 * E o que ela NÃO faz: proteger o `/auth/login`. Quem ataca manda `curl` e não
 * passa por aqui — ver o cabeçalho de `login-guard.ts`.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { motion } from "motion/react";
import { z } from "zod";
import { ApiError } from "../lib/api.ts";
import { LoginGuard, MAX_FAILURES, explain, formatWait, type Decision } from "../lib/login-guard.ts";
import { useAuthStore } from "../store/auth.store.ts";
import { Button, Spinner } from "./ui";
import { ThemeToggle } from "./ThemeToggle";

const credentialsSchema = z.object({
  email: z.email("Informe um e-mail válido."),
  password: z.string().min(1, "Informe a senha."),
});

export function LoginScreen() {
  const login = useAuthStore((state) => state.login);
  const status = useAuthStore((state) => state.status);
  const serverError = useAuthStore((state) => state.error);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Uma instância por montagem da tela; o estado real mora no armazenamento,
  // então recarregar a página não zera nada.
  const guard = useMemo(() => new LoginGuard(), []);
  /** A decisão que está impedindo agora. `null` = pode tentar. */
  const [gate, setGate] = useState<Decision | null>(null);
  /** Quantas tentativas ainda cabem, mostrado depois da primeira falha. */
  const [remaining, setRemaining] = useState<number | null>(null);

  const bloqueado = gate !== null;

  // O freio é por CONTA: trocar o e-mail tem que trocar o que a tela mostra,
  // senão o bloqueio de uma conta pareceria valer para a outra.
  useEffect(() => {
    const decision = guard.check(email);
    setGate(decision.allowed ? null : decision);
    setRemaining(decision.failures > 0 ? decision.remainingAttempts : null);
  }, [email, guard]);

  // Relógio só enquanto há bloqueio: um `setInterval` permanente na tela de
  // login é CPU queimada em todo painel aberto no login o dia inteiro.
  useEffect(() => {
    if (!bloqueado) return;
    const timer = setInterval(() => {
      const decision = guard.check(email);
      setGate(decision.allowed ? null : decision);
    }, 500);
    return () => clearInterval(timer);
  }, [bloqueado, email, guard]);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = credentialsSchema.safeParse({ email, password });
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message ?? "Dados inválidos.");
      return;
    }

    // A trava está AQUI, no caminho do envio — não no `disabled` do botão.
    const decision = guard.check(parsed.data.email);
    if (!decision.allowed) {
      setGate(decision);
      return;
    }

    setLocalError(null);
    guard.recordAttempt();
    try {
      await login(parsed.data.email, parsed.data.password);
      guard.recordSuccess(parsed.data.email);
    } catch (error) {
      // SÓ credencial recusada conta. Rede fora do ar (status 0) e erro do
      // servidor não são senha errada, e bloquear por eles seria punir o
      // operador por um problema que não é dele.
      if (error instanceof ApiError && error.status === 401) {
        const next = guard.recordFailure(parsed.data.email);
        setGate(next);
        setRemaining(next.remainingAttempts);
        setPassword("");
      }
    }
  }

  const loading = status === "loading";
  const message = localError ?? serverError;

  // "Faltam 4 tentativas" é o que importa depois de errar a senha; o relógio é
  // secundário e já está no botão. Por isso os dois aparecem juntos no atraso,
  // e só o bloqueio (ou o limite por janela) fala sozinho — ali o que a pessoa
  // precisa saber é quanto falta e a quem recorrer.
  const faltam =
    remaining === null || remaining >= MAX_FAILURES || remaining <= 0
      ? null
      : remaining === 1
        ? "Falta 1 tentativa antes do bloqueio de 10 minutos."
        : `Faltam ${remaining} tentativas antes do bloqueio de 10 minutos.`;
  const aviso = gate
    ? gate.reason === "delay"
      ? [`Aguarde ${formatWait(gate.waitMs)}.`, faltam].filter(Boolean).join(" ")
      : explain(gate)
    : faltam;

  return (
    <div className="relative grid min-h-full grid-cols-1 bg-app lg:grid-cols-2">
      {/* ------------------------------------------------------ formulário */}
      <div className="flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-sm"
        >
          {/* A marca aparece aqui só no estreito, onde a coluna da direita não
              cabe. Acima de `lg` ela some daqui e vive lá — some do DOM, não
              fica escondida, senão o nome do painel apareceria duas vezes para
              leitor de tela. */}
          <div className="mb-8 lg:hidden">
            <BrandMark />
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-ink">Entrar</h1>
          <p className="mt-1.5 text-sm text-muted">Informe suas credenciais para acessar o painel.</p>

          {/* `noValidate`: sem isto a validação nativa do navegador bloqueia o envio
              ANTES do Zod, e a mensagem que aparece é a do navegador — em outro
              idioma e com outro texto. Uma fonte só de validação. */}
          <form onSubmit={onSubmit} noValidate className="mt-7 space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-ink">
                E-mail <span className="text-rose-500">*</span>
              </span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                placeholder="operador@empresa.com.br"
                className="mt-1.5 w-full rounded-lg border border-line bg-panel px-3.5 py-2.5 text-sm text-ink placeholder:text-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-ink">
                Senha <span className="text-rose-500">*</span>
              </span>
              {/* O olho é um <button>, e não mais um <input>: a ordem dos
                  campos é contrato com quem automatiza a tela, e um input a
                  mais aqui deslocaria tudo. */}
              <div className="relative mt-1.5">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  placeholder="Sua senha"
                  className="w-full rounded-lg border border-line bg-panel py-2.5 pl-3.5 pr-11 text-sm text-ink placeholder:text-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-faint transition-colors hover:text-muted"
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </label>

            {message ? (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                role="alert"
                className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-600 dark:text-rose-300"
              >
                {message}
              </motion.p>
            ) : null}

            {/* `polite` e não `alert`: o contador muda a cada meio segundo, e um
                `alert` faria o leitor de tela repetir a frase sem parar. */}
            {aviso ? (
              <p
                aria-live="polite"
                className={
                  gate
                    ? "rounded-lg bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-700 dark:text-amber-300"
                    : "px-1 text-xs leading-relaxed text-muted"
                }
              >
                {aviso}
              </p>
            ) : null}

            <Button type="submit" disabled={loading || bloqueado} className="w-full py-2.5">
              {loading ? (
                <Spinner className="border-app/40 border-t-app" />
              ) : gate ? (
                `Aguarde ${formatWait(gate.waitMs)}`
              ) : (
                "Entrar"
              )}
            </Button>
          </form>

          <p className="mt-6 text-xs leading-relaxed text-muted">
            Acesso é criado pelo administrador do sistema. Sem credencial, procure quem opera o servidor —
            não há autoatendimento.
          </p>
        </motion.div>
      </div>

      {/* --------------------------------------------------------- a marca */}
      {/* Superfície de marca: escura nos dois temas, de propósito. É o único
          lugar do painel que não acompanha o tema, porque aqui ela é identidade
          e não interface — e some por inteiro abaixo de `lg`. */}
      <div className="relative hidden overflow-hidden bg-[#141a35] lg:flex lg:items-center lg:justify-center lg:border-l lg:border-white/10">
        <GridPattern />
        <div className="relative z-10 max-w-md px-8 text-center">
          <BrandMark large />
        </div>
      </div>

      {/* O tema é escolhível ANTES de entrar: quem abre numa sala clara não
          deveria precisar autenticar para poder enxergar a tela. */}
      <div className="absolute bottom-5 right-5 z-20">
        <ThemeToggle />
      </div>
    </div>
  );
}

/**
 * A marca do sistema: **MOB Telemetria**.
 *
 * `large` é a versão da coluna escura — o logotipo inteiro, grande, no único
 * lugar do painel feito para ele. A pequena fica no topo do formulário quando a
 * coluna escura não cabe, e leva a descrição junto: no estreito ela é a única
 * pista do que é este sistema.
 *
 * ## Dois arquivos, e por quê
 *
 * `logo.webp` traz o símbolo **e** o nome desenhado. `logo-mark.webp` é só o
 * símbolo: abaixo de uns 48 px o "MOB TELEMETRIA" do logotipo completo vira um
 * borrão cinza, então ali o nome vai como texto ao lado — que ainda busca,
 * seleciona e é lido por leitor de tela.
 *
 * Os dois têm fundo azul-escuro próprio (o logotipo veio assim, com gradiente —
 * não há recorte limpo possível). Em vez de disfarçar, a imagem é apresentada
 * como **placa**: cantos arredondados e um fio de contorno. Fica igual no tema
 * claro e no escuro, que é o que se espera de uma marca.
 *
 * O `alt` do grande carrega o nome, e por isso ele não se repete como texto: a
 * coluna anunciaria "MOB Telemetria" duas vezes para quem usa leitor de tela.
 */
function BrandMark({ large = false }: { large?: boolean }) {
  if (large) {
    return (
      <>
        <div className="relative mx-auto w-72 xl:w-80">
          {/* Halo: separa a placa escura do fundo escuro. Sem ele o logotipo
              encosta na grade e perde o contorno. `blur` em elemento fixo, sem
              animação — fundo que pulsa cansa quem olha a tela o dia todo. */}
          <div aria-hidden className="absolute -inset-8 rounded-[3rem] bg-sky-500/20 blur-3xl" />
          <img
            src="/logo.webp"
            alt="MOB Telemetria"
            width={512}
            height={512}
            decoding="async"
            className="relative w-full rounded-3xl shadow-2xl shadow-black/50 ring-1 ring-white/15"
          />
        </div>
        <p className="mt-8 text-sm leading-relaxed text-white/70">Painel de operação da frota</p>
      </>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <img
        src="/logo-mark.webp"
        alt=""
        aria-hidden
        width={256}
        height={256}
        decoding="async"
        className="size-11 shrink-0 rounded-xl ring-1 ring-black/10 dark:ring-white/10"
      />
      <div className="min-w-0">
        <div className="text-lg font-semibold tracking-tight text-ink">MOB Telemetria</div>
        <div className="text-xs text-muted">Painel de operação da frota</div>
      </div>
    </div>
  );
}

/**
 * Grade do fundo, em SVG: quadrados finos e alguns blocos acesos, como no
 * gabarito. SVG embutido em vez de imagem — são 20 linhas e nenhuma requisição.
 */
function GridPattern() {
  return (
    <svg aria-hidden className="absolute inset-0 size-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="grade-login" width="52" height="52" patternUnits="userSpaceOnUse">
          <path d="M52 0H0V52" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grade-login)" />
      {/* Blocos acesos, em posições fixas: aleatório mudaria a cada render e
          faria o fundo piscar sem motivo. */}
      {[
        [156, 52], [364, 104], [104, 364], [312, 468], [416, 260], [52, 572],
      ].map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="52" height="52" fill="rgba(255,255,255,0.035)" />
      ))}
    </svg>
  );
}

/** Olho aberto/fechado do campo de senha. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="m4 4 16 16" strokeLinecap="round" /> : null}
    </svg>
  );
}
